/**
 * Dining availability view-model utilities.
 *
 * These helpers convert raw slot lists into data structures optimized for
 * day/week selectors and timeslot rendering in dining flow steps.
 */

import { SlotThematicAvailabilityI, DateAvailability } from '@msc-b2c/interfaces/interfaces';
import { formatTime } from '@msc-b2c/shared-utils';

export type { DateAvailability };

/**
 * Builds a date-indexed map from slot list.
 *
 * Per date we track:
 * - full slot list,
 * - total slot count,
 * - available slot count,
 * - quick hasAvailableSlots flag for UI enable/disable.
 */
export const buildDateAvailabilityMap = (
  allSlots: SlotThematicAvailabilityI[]
): Map<string, DateAvailability> => {
  const map = new Map<string, DateAvailability>();

  if (!allSlots) {
    return map;
  }

  for (const slot of allSlots) {
    const date = slot.date;

    if (!map.has(date)) {
      map.set(date, {
        date,
        hasAvailableSlots: false,
        totalSlots: 0,
        availableSlots: 0,
        slots: [],
      });
    }

    const availability = map.get(date);
    if (availability) {
      availability.slots.push(slot);
      availability.totalSlots += 1;

      if (slot.isReserved === false) {
        availability.availableSlots += 1;
        availability.hasAvailableSlots = true;
      }
    }
  }

  return map;
};

/**
 * Returns whether at least one slot is available for a date.
 */
export const isDayAvailable = (
  date: string,
  availabilityMap: Map<string, DateAvailability>
): boolean => {
  const dayAvail = availabilityMap.get(date);
  return dayAvail?.hasAvailableSlots ?? false;
};

/**
 * Returns whether any date in a group has availability.
 * Used by week/group pills to determine enabled state.
 */
export const hasAvailabilityInDateSet = (
  dates: string[],
  availabilityMap: Map<string, DateAvailability>
): boolean => {
  if (!dates || dates.length === 0) {
    return false;
  }

  return dates.some((date) => isDayAvailable(date, availabilityMap));
};

/**
 * Gets timeslots for one date.
 *
 * onlyAvailable=true filters out reserved/unavailable slots for simplified UI lists.
 */
export const getTimeslotsForDate = (
  date: string,
  availabilityMap: Map<string, DateAvailability>,
  onlyAvailable = false
): SlotThematicAvailabilityI[] => {
  const dayAvail = availabilityMap.get(date);

  if (!dayAvail) {
    return [];
  }

  if (!onlyAvailable) {
    return dayAvail.slots;
  }

  return dayAvail.slots.filter((slot) => slot.isReserved === false);
};

/**
 * Composite key used to cache thematic slots by (restaurant,date).
 */
export const buildDiningRoomDateAvailabilityKey = (
  diningRoomCode: string | undefined,
  date: string
): string | undefined => {
  if (!diningRoomCode || !date) {
    return undefined;
  }

  return `${diningRoomCode}::${date}`;
};

/**
 * Builds a merged availability map accounting for INT136-validated per-restaurant/per-date slots.
 *
 * Logic:
 * - Start with bootstrap availability map (from INT130/INT135)
 * - For each date, check if ANY restaurant has slots in thematicSlotsByDiningRoomDate
 * - If NO restaurants have slots for a date (even though bootstrap suggested availability),
 *   override to mark the date as unavailable
 * - This ensures dates are disabled when all restaurants have zero availability in INT136
 */
export const buildMergedAvailabilityMapWithThematicValidation = ({
  bootstrapAvailabilityMap,
  itineraryDates,
  availableDiningRoomCodes,
  thematicSlotsByDiningRoomDate,
}: {
  bootstrapAvailabilityMap: Map<string, DateAvailability>;
  itineraryDates: string[];
  availableDiningRoomCodes: (string | undefined)[];
  thematicSlotsByDiningRoomDate: Record<string, unknown[]>;
}): Map<string, DateAvailability> => {
  const mergedMap = new Map<string, DateAvailability>(bootstrapAvailabilityMap);

  for (const date of itineraryDates) {
    // Check if ANY restaurant has slots for this date in INT136 data
    const hasAnyRestaurantSlot = availableDiningRoomCodes.some((roomCode) => {
      const roomDateKey = buildDiningRoomDateAvailabilityKey(roomCode, date);
      const slots = roomDateKey ? thematicSlotsByDiningRoomDate[roomDateKey] : undefined;
      return Array.isArray(slots) && slots.length > 0;
    });

    // If no restaurant has slots for this date, mark it unavailable
    if (!hasAnyRestaurantSlot) {
      const existingAvailability = mergedMap.get(date);
      if (existingAvailability) {
        mergedMap.set(date, {
          ...existingAvailability,
          hasAvailableSlots: false,
          availableSlots: 0,
        });
      } else {
        // Date not in bootstrap map, add it as unavailable
        mergedMap.set(date, {
          date,
          hasAvailableSlots: false,
          totalSlots: 0,
          availableSlots: 0,
          slots: [],
        });
      }
    }
  }

  return mergedMap;
};

/**
 * Merges fallback times (INT130/INT135) with thematic response (INT136).
 *
 * Merge policy:
 * - reduce-only: do not introduce times missing from fallback list,
 * - if thematic slot exists for a fallback time -> mark available,
 * - if thematic slot missing -> mark unavailable.
 */
export const mergeFallbackTimesWithThematicAvailability = ({
  fallbackTimes,
  thematicTimes,
  isThematicAvailabilityResolved = false,
}: {
  fallbackTimes: readonly string[];
  thematicTimes: readonly SlotThematicAvailabilityI[];
  isThematicAvailabilityResolved?: boolean;
}): SlotThematicAvailabilityI[] => {
  const hasThematicAvailability = thematicTimes.length > 0;
  // Once INT136 has resolved for this room/date, any missing fallback slot is unavailable.
  const shouldMarkMissingAsUnavailable = isThematicAvailabilityResolved || hasThematicAvailability;
  const thematicTimesByKey = thematicTimes
    .filter((slot) => Boolean(slot.time))
    .reduce<Map<string, SlotThematicAvailabilityI>>((acc, slot) => {
      const existing = acc.get(slot.time);
      if (!existing) {
        acc.set(slot.time, slot);
        return acc;
      }

      if (existing.isReserved && slot.isReserved === false) {
        acc.set(slot.time, slot);
      }

      return acc;
    }, new Map());

  const mergedTimes = Array.from(new Set(fallbackTimes.filter(Boolean)));

  return mergedTimes.map((time) => {
    const thematicSlot = thematicTimesByKey.get(time);

    if (thematicSlot) {
      return {
        ...thematicSlot,
        isReserved: false,
      };
    }

    return {
      time,
      isReserved: shouldMarkMissingAsUnavailable,
    };
  });
};

/**
 * Normalizes `timeSlots` values coming from backend payloads.
 * - Accepts arrays or strings (e.g. the legacy "N.A.") and returns a string[].
 */
export const normalizeTimeSlots = (timeSlots?: readonly string[] | string | null): string[] => {
  if (!timeSlots) return [];

  if (Array.isArray(timeSlots)) {
    return timeSlots.filter(Boolean) as string[];
  }

  if (typeof timeSlots === 'string') {
    const trimmed = timeSlots.trim();
    if (trimmed.length === 0) return [];
    // Treat explicit legacy sentinel as empty array
    if (trimmed.toUpperCase() === 'N.A.' || trimmed.toUpperCase() === 'NA') return [];
    // If server sent a comma separated list mistakenly, try to split it
    if (trimmed.includes(',')) {
      return trimmed
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
    // Unknown single string that is not the sentinel: return empty to be safe
    return [];
  }

  return [];
};

/**
 * Formats HH:mm time using context-aware locale/time format settings.
 */
export const formatSlotTime = (time: string): string => formatTime(time);

/**
 * Returns a chronologically sorted copy of slots.
 */
export const sortSlotsByTime = (
  slots: SlotThematicAvailabilityI[]
): SlotThematicAvailabilityI[] => {
  if (!slots) {
    return [];
  }

  return [...slots].sort((a, b) => {
    const timeA = a.time || '';
    const timeB = b.time || '';
    return timeA.localeCompare(timeB);
  });
};
